import React from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '../components/UI';
import { AdPlaceholder } from '../components/UI';

// ── Mock region card for landing page preview ─────────────────────────────────
function MockRegionCard({ icon, region, type, players }) {
  return (
    <div className="bg-white rounded-2xl border border-amber-100 shadow-sm overflow-hidden">
      <div className="px-4 py-3 bg-green-50 border-b border-amber-100 flex items-center gap-2">
        <span className="text-base">{icon}</span>
        <div>
          <p className="font-serif font-semibold text-green-900 text-sm">{region}</p>
          <p className="text-xs text-gray-400 capitalize">{type}</p>
        </div>
      </div>
      <div className="px-4 py-2 divide-y divide-amber-50">
        {players.map((p, i) => (
          <div key={i} className="flex items-center gap-2.5 py-2">
            <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold flex-shrink-0 ${
              i === 0 ? 'bg-amber-100 text-amber-700' :
              i === 1 ? 'bg-gray-100 text-gray-500' :
              i === 2 ? 'bg-orange-50 text-orange-500' : 'bg-green-50 text-green-700'
            }`}>{i + 1}</span>
            <div className="w-6 h-6 rounded-full bg-green-800 text-amber-100 flex items-center justify-center text-xs font-semibold border border-amber-200 flex-shrink-0">
              {p.initials}
            </div>
            <span className="text-sm font-medium text-green-900 flex-1">{p.name}</span>
            <span className={`text-xs font-semibold ${p.score < 0 ? 'text-green-700' : p.score > 0 ? 'text-red-500' : 'text-gray-600'}`}>
              {p.score === 0 ? 'E' : p.score > 0 ? `+${p.score}` : p.score}
            </span>
            <span className="text-xs text-gray-400">{p.rps} RPS</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen flex flex-col">
      {/* Nav */}
      <nav className="bg-white/80 backdrop-blur-sm border-b border-amber-100 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-5 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Logo size="sm" />
            <span className="font-serif font-bold text-green-900 text-lg">GolfBoard Scores</span>
          </div>
          <Link to="/login" className="btn-primary text-sm">
            Sign In
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative flex-1 flex items-center justify-center text-center px-5 py-20 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-green-100 rounded-full opacity-30 blur-3xl" />
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-amber-100 rounded-full opacity-40 blur-2xl" />
        </div>
        <div className="relative max-w-2xl mx-auto">
          <Logo size="lg" className="mx-auto mb-5" />
          <h1 className="font-serif font-bold text-4xl sm:text-5xl text-green-900 leading-tight mb-4">
            Your Game.<br />Your Leaderboard.
          </h1>
          <p className="text-gray-600 text-lg leading-relaxed mb-8 max-w-xl mx-auto">
            GolfBoard Scores lets amateur golfers post scores at any course in the world and see how they stack up against other players — no tournament, no pressure, just the game you love.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/login" className="btn-gold text-base px-8 py-3 w-full sm:w-auto">
              Create Free Account
            </Link>
            <Link to="/login" className="btn-secondary text-base px-8 py-3 w-full sm:w-auto">
              Sign In
            </Link>
          </div>
          <p className="text-sm text-gray-400 mt-4">Free forever. No subscription required.</p>

          {/* Public leaderboard CTA */}
          <div className="mt-6 inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-5 py-3">
            <span className="text-lg">🏆</span>
            <span className="text-sm text-amber-800">
              Want to browse leaderboards without signing up?{' '}
              <Link to="/public/leaderboard/browse" className="font-semibold text-amber-700 hover:underline">
                View public leaderboards →
              </Link>
            </span>
          </div>
        </div>
      </section>

      {/* Ad slot — above the fold on landing */}
      <div className="max-w-4xl mx-auto w-full px-5 pb-6">
        <AdPlaceholder label="Advertisement" className="h-24" />
      </div>

      {/* How it works */}
      <section className="bg-white border-t border-amber-100 py-16 px-5">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-serif font-bold text-3xl text-green-900 text-center mb-2">How It Works</h2>
          <p className="text-gray-500 text-center mb-12">Three simple steps to get on the board.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {[
              { icon: '🔍', step: '1', title: 'Find Your Course', desc: 'Search from thousands of courses worldwide by name, city, state, or country. If it\'s not listed, add it in seconds.' },
              { icon: '📝', step: '2', title: 'Post Your Score', desc: 'Select your tee, enter your total score, and we\'ll handle the over/under calculation automatically.' },
              { icon: '🏆', step: '3', title: 'Climb the Board', desc: 'See where your score ranks against other amateur golfers on that exact tee. Add friends to filter the leaderboard and see exactly how you stack up against your regular playing partners.' },
            ].map(item => (
              <div key={item.step} className="text-center">
                <div className="w-14 h-14 rounded-full bg-green-50 border-2 border-green-100 flex items-center justify-center text-2xl mx-auto mb-4">
                  {item.icon}
                </div>
                <h3 className="font-serif font-semibold text-green-900 text-lg mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── LOCAL LEGENDS FEATURE SECTION ───────────────────────────────────── */}
      <section className="py-20 px-5 bg-gradient-to-br from-green-900 via-green-800 to-green-900 overflow-hidden relative">
        {/* Background decoration */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-20 -right-20 w-96 h-96 bg-amber-400/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-10 -left-10 w-72 h-72 bg-green-600/30 rounded-full blur-2xl" />
        </div>

        <div className="max-w-5xl mx-auto relative">
          {/* Badge */}
          <div className="flex justify-center mb-5">
            <span className="inline-flex items-center gap-2 bg-amber-400/20 border border-amber-400/40 text-amber-300 text-xs font-semibold uppercase tracking-widest rounded-full px-4 py-1.5">
              🏅 New Feature
            </span>
          </div>

          {/* Headline */}
          <div className="text-center mb-4">
            <h2 className="font-serif font-bold text-4xl sm:text-5xl text-white leading-tight mb-4">
              Be the Best Golfer<br />
              <span className="text-amber-300">In Your City.</span>
            </h2>
            <p className="text-green-200 text-lg leading-relaxed max-w-2xl mx-auto">
              Local Legends is a first-of-its-kind regional leaderboard that ranks the top golfers in every city, state, and country — based on courses right in your backyard.
            </p>
          </div>

          {/* Three sub-hooks */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-14 mt-10">
            {[
              { icon: '🏙️', title: 'Your City', desc: 'Compete for the #1 spot among everyone who plays your local courses. Real bragging rights, no tournament required.' },
              { icon: '📍', title: 'Your State', desc: 'See how you stack up against golfers across your entire state. Monthly, yearly, or all-time — you decide.' },
              { icon: '🌎', title: 'Your Country', desc: 'Zoom out and see where you rank nationally. One leaderboard, every amateur golfer on the platform.' },
            ].map(f => (
              <div key={f.title} className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/10">
                <div className="text-3xl mb-3">{f.icon}</div>
                <h4 className="font-serif font-semibold text-amber-200 text-base mb-2">{f.title}</h4>
                <p className="text-green-300 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>

          {/* Mock leaderboard preview */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
            <MockRegionCard
              icon="🏙️" region="Louisville, KY" type="city"
              players={[
                { name: 'tiger_woods', initials: 'TW', score: -4, rps: 94.2 },
                { name: 'grip_it_rip_it', initials: 'GR', score: -2, rps: 87.1 },
                { name: 'fairway_frank', initials: 'FF', score: 1, rps: 79.5 },
                { name: 'bogey_bob', initials: 'BB', score: 3, rps: 71.0 },
                { name: 'double_eagle_dan', initials: 'DE', score: 4, rps: 65.8 },
              ]}
            />
            <MockRegionCard
              icon="📍" region="Kentucky" type="state"
              players={[
                { name: 'caddie_king', initials: 'CK', score: -6, rps: 98.7 },
                { name: 'tiger_woods', initials: 'TW', score: -4, rps: 94.2 },
                { name: 'ace_hunter', initials: 'AH', score: -3, rps: 90.1 },
                { name: 'green_machine', initials: 'GM', score: 0, rps: 83.4 },
                { name: 'par_seeker', initials: 'PS', score: 2, rps: 76.9 },
              ]}
            />
            <MockRegionCard
              icon="🌎" region="United States" type="country"
              players={[
                { name: 'hole_in_one_hank', initials: 'HH', score: -8, rps: 99.9 },
                { name: 'caddie_king', initials: 'CK', score: -6, rps: 98.7 },
                { name: 'eagle_eye_ed', initials: 'EE', score: -5, rps: 96.3 },
                { name: 'tiger_woods', initials: 'TW', score: -4, rps: 94.2 },
                { name: 'ace_hunter', initials: 'AH', score: -3, rps: 90.1 },
              ]}
            />
          </div>

          {/* Bottom CTA */}
          <div className="text-center">
            <p className="text-green-300 text-sm mb-5">
              Rankings update automatically as players post scores. <span className="text-amber-300 font-medium">Your next round could put you on top.</span>
            </p>
            <Link to="/login" className="btn-gold text-base px-10 py-3 inline-block">
              Claim Your Spot →
            </Link>
          </div>
        </div>
      </section>

      {/* ── BADGE SYSTEM FEATURE SECTION ──────────────────────────────────────── */}
      <section className="py-20 px-5 bg-white border-t border-amber-100 overflow-hidden relative">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-1/2 -right-32 -translate-y-1/2 w-96 h-96 bg-amber-50 rounded-full opacity-70 blur-3xl" />
          <div className="absolute -bottom-10 left-0 w-72 h-72 bg-green-50 rounded-full opacity-60 blur-2xl" />
        </div>

        <div className="max-w-5xl mx-auto relative">
          {/* Label */}
          <div className="flex justify-center mb-5">
            <span className="inline-flex items-center gap-2 bg-green-50 border border-green-200 text-green-800 text-xs font-semibold uppercase tracking-widest rounded-full px-4 py-1.5">
              🏅 Earn Badges
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: copy */}
            <div>
              <h2 className="font-serif font-bold text-4xl sm:text-5xl text-green-900 leading-tight mb-5">
                Play. Earn.<br />
                <span className="text-amber-600">Show It Off.</span>
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                GolfBoard Scores rewards your progress with 23 collectible badges — earned by posting rounds, exploring courses, dominating leaderboards, and building your reputation in the community.
              </p>
              <div className="space-y-3 mb-8">
                {[
                  { icon: '🏌️', text: 'Post your first score and earn the First Tee badge instantly' },
                  { icon: '🏆', text: 'Climb to #1 on your city, state, or country leaderboard' },
                  { icon: '🚩', text: 'Be the first to post on a course and claim Course Pioneer' },
                  { icon: '🔗', text: 'Share any badge you earn with a single tap' },
                ].map(item => (
                  <div key={item.text} className="flex items-start gap-3">
                    <span className="text-xl flex-shrink-0 mt-0.5">{item.icon}</span>
                    <p className="text-gray-600 text-sm leading-relaxed">{item.text}</p>
                  </div>
                ))}
              </div>
              <Link to="/login" className="btn-gold text-base px-8 py-3 inline-block">
                Start Earning Badges →
              </Link>
            </div>

            {/* Right: mock badge grid */}
            <div className="relative">
              <div className="grid grid-cols-4 gap-3">
                {[
                  { name: 'First Tee',        emoji: '🏌️', earned: true,  color: 'bg-amber-50  border-amber-200' },
                  { name: 'Course Explorer',  emoji: '⛳',  earned: true,  color: 'bg-amber-50  border-amber-200' },
                  { name: 'City Champion',    emoji: '🏆', earned: true,  color: 'bg-amber-50  border-amber-200' },
                  { name: 'Verified Round',   emoji: '✅', earned: true,  color: 'bg-amber-50  border-amber-200' },
                  { name: 'Regular Golfer',   emoji: '📋', earned: true,  color: 'bg-amber-50  border-amber-200' },
                  { name: 'Hot Streak',       emoji: '🔥', earned: true,  color: 'bg-amber-50  border-amber-200' },
                  { name: 'Course Pioneer',   emoji: '🚩', earned: false, color: 'bg-gray-50   border-gray-200' },
                  { name: 'Trusted Player',   emoji: '⭐', earned: false, color: 'bg-gray-50   border-gray-200' },
                  { name: 'State Champion',   emoji: '📍', earned: false, color: 'bg-gray-50   border-gray-200' },
                  { name: 'Local Traveler',   emoji: '✈️', earned: false, color: 'bg-gray-50   border-gray-200' },
                  { name: 'Dedicated Player', emoji: '🎯', earned: false, color: 'bg-gray-50   border-gray-200' },
                  { name: 'Nat\'l Champion',  emoji: '🌎', earned: false, color: 'bg-gray-50   border-gray-200' },
                ].map((badge, i) => (
                  <div
                    key={i}
                    className={`rounded-2xl border ${badge.color} p-3 flex flex-col items-center gap-1.5 transition-all duration-200 ${badge.earned ? 'shadow-sm' : ''}`}
                    style={{ opacity: badge.earned ? 1 : 0.45 }}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl ${badge.earned ? 'bg-white shadow-sm' : 'bg-gray-100'}`}>
                      {badge.emoji}
                    </div>
                    <span className="text-center font-medium text-green-900 leading-tight" style={{ fontSize: '0.6rem' }}>
                      {badge.name}
                    </span>
                    {badge.earned && (
                      <span className="text-xs text-amber-600 font-semibold" style={{ fontSize: '0.55rem' }}>EARNED</span>
                    )}
                  </div>
                ))}
              </div>
              {/* decorative count pill */}
              <div className="absolute -bottom-4 -right-4 bg-green-900 text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg border-2 border-white">
                23 badges to collect
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-5 bg-green-900">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-serif font-bold text-3xl text-amber-200 text-center mb-2">Built for Amateur Golfers</h2>
          <p className="text-green-300 text-center mb-12">All the competition, none of the pressure.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {[
              { icon: '🌍', title: 'Thousands of Courses', desc: 'Search a global database of golf courses. Every tee, rating, and yardage included.' },
              { icon: '🤝', title: 'Built on Trust', desc: 'Scores are self-reported and honor-based — just like the spirit of the game itself.' },
              { icon: '🏅', title: 'Real Rankings', desc: 'Tied scores share the same rank, exactly like a real tournament leaderboard.' },
              { icon: '👥', title: 'Play With Friends', desc: 'Add friends and filter any leaderboard to see only your group\'s scores — perfect for settling who really owns your home course.' },
              { icon: '📱', title: 'Mobile Friendly', desc: 'Post a score right from the course. GolfBoard works on any device, any screen size.' },
              { icon: '🏅', title: 'Earn Badges', desc: '23 collectible badges for posting rounds, conquering courses, topping leaderboards, building community trust, and making friends.' },
            ].map(f => (
              <div key={f.title} className="flex gap-4 items-start bg-green-800/50 rounded-xl p-4">
                <span className="text-2xl flex-shrink-0">{f.icon}</span>
                <div>
                  <h4 className="font-semibold text-amber-200 mb-1">{f.title}</h4>
                  <p className="text-green-300 text-sm leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community quote */}
      <section className="py-16 px-5 bg-amber-50 border-t border-amber-100">
        <div className="max-w-2xl mx-auto text-center">
          <div className="text-4xl mb-4">💬</div>
          <blockquote className="font-serif text-xl text-green-900 leading-relaxed mb-4">
            "Golf is a game of integrity. GolfBoard Scores extends that to the leaderboard — your score, your word, your community."
          </blockquote>
          <p className="text-gray-400 text-sm">— The GolfBoard Scores Team</p>
        </div>
      </section>

      {/* Ad slot — mid-page */}
      <div className="max-w-4xl mx-auto w-full px-5 py-6 bg-white border-t border-amber-100">
        <AdPlaceholder label="Advertisement" className="h-24" />
      </div>

      {/* CTA */}
      <section className="py-16 px-5 bg-white border-t border-amber-100 text-center">
        <div className="max-w-xl mx-auto">
          <h2 className="font-serif font-bold text-3xl text-green-900 mb-3">Ready to get on the board?</h2>
          <p className="text-gray-500 mb-7">Join amateur golfers worldwide. It only takes a minute to sign up.</p>
          <Link to="/login" className="btn-gold text-base px-10 py-3 inline-block">
            Create Your Free Account
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-green-900 text-green-300 py-8 px-5">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-2">
            <Logo size="sm" />
            <span className="font-serif font-semibold text-amber-200">GolfBoard Scores</span>
          </div>
          <div className="flex gap-5">
            <Link to="/login" className="hover:text-amber-200 transition-colors">Sign In</Link>
            <Link to="/terms" className="hover:text-amber-200 transition-colors">Terms & Conditions</Link>
            <Link to="/privacy" className="hover:text-amber-200 transition-colors">Privacy Policy</Link>
            <Link to="/contact" className="hover:text-amber-200 transition-colors">Contact</Link>
            <Link to="/donate" className="hover:text-amber-200 transition-colors text-amber-400">☕ Donate</Link>
          </div>
          <p className="text-green-500 text-xs">© {new Date().getFullYear()} GolfBoard Scores. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}